import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { lineDigest, type Script, type ScriptOp } from '@reelmachine/core';
import { isApiError } from '../../api/client';
import { keys, setScript, useApi } from '../../api/hooks';
import { useToast } from '../../ui/Toast';

export type EditableField = 'text' | 'onScreen' | 'visual';
const OP: Record<EditableField, 'setText' | 'setOnScreen' | 'setVisual'> = {
  text: 'setText',
  onScreen: 'setOnScreen',
  visual: 'setVisual',
};

type FieldOp = Extract<ScriptOp, { op: 'setText' | 'setOnScreen' | 'setVisual' }>;

export const SAVE_DEBOUNCE_MS = 700;
export const fieldKey = (field: EditableField, lineId: string) => `${field}:${lineId}`;

export interface ScriptEditor {
  value(lineId: string, field: EditableField): string;
  set(lineId: string, field: EditableField, value: string): void;
  /** Saves pending edits now and resolves when everything is saved (or failed). */
  flush(): Promise<void>;
  /** Removes a line (after saving pending edits). */
  remove(lineId: string): Promise<void>;
  saving: boolean;
}

/**
 * Local drafts on top of the server script. Edits are collected per field and sent as one `patchScript`
 * with `baseVersion` after a short pause. Saves run one after another. On 409 the latest script is loaded
 * and the drafts are dropped.
 */
export function useScriptEditor(reelId: string, script: Script | undefined): ScriptEditor {
  const api = useApi();
  const qc = useQueryClient();
  const toast = useToast();
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const pending = useRef(new Map<string, FieldOp>());
  const scriptRef = useRef(script);
  const versionRef = useRef(script?.version ?? 0);
  const chain = useRef<Promise<void>>(Promise.resolve());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  scriptRef.current = script;

  // A new version from elsewhere (save response, chat, refetch): keep only drafts that are still unsaved.
  useEffect(() => {
    if (!script) return;
    versionRef.current = script.version;
    setDrafts((d) => {
      const next = Object.fromEntries(Object.entries(d).filter(([k]) => pending.current.has(k)));
      return Object.keys(next).length === Object.keys(d).length ? d : next;
    });
  }, [script, script?.version]);

  const doSave = useCallback(async () => {
    const current = scriptRef.current;
    if (!current || pending.current.size === 0) return;
    const batch = [...pending.current.entries()];
    pending.current.clear();
    const ops = batch
      .map(([, op]) => op)
      .filter((op) => {
        const line = current.lines.find((l) => l.id === op.lineId);
        const field = (Object.keys(OP) as EditableField[]).find((f) => OP[f] === op.op)!;
        return line && line[field] !== op.value;
      });
    if (ops.length === 0) {
      setDrafts((d) => Object.fromEntries(Object.entries(d).filter(([k]) => pending.current.has(k))));
      return;
    }
    setSaving(true);
    try {
      const res = await api.call('patchScript', { reelId }, { baseVersion: versionRef.current, ops });
      versionRef.current = res.script.version;
      scriptRef.current = res.script;
      setScript(qc, reelId, res.script);
      void qc.invalidateQueries({ queryKey: keys.events(reelId) });
    } catch (e) {
      if (isApiError(e, 'conflict')) {
        pending.current.clear();
        setDrafts({});
        toast('Somebody else changed the script. Showing the latest version.');
        await qc.invalidateQueries({ queryKey: keys.reel(reelId) });
      } else {
        // Put the edits back unless they were overwritten meanwhile, so the next save retries them.
        for (const [k, op] of batch) if (!pending.current.has(k)) pending.current.set(k, op);
        toast(`Could not save your edit. ${e instanceof Error ? e.message : ''}`.trim());
      }
    } finally {
      setSaving(false);
    }
  }, [api, qc, reelId, toast]);

  const save = useCallback(() => {
    chain.current = chain.current.then(doSave, doSave);
    return chain.current;
  }, [doSave]);

  const flush = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    return save();
  }, [save]);

  const set = useCallback(
    (lineId: string, field: EditableField, value: string) => {
      const k = fieldKey(field, lineId);
      setDrafts((d) => ({ ...d, [k]: value }));
      pending.current.set(k, { op: OP[field], lineId, value });
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        timer.current = null;
        void save();
      }, SAVE_DEBOUNCE_MS);
    },
    [save],
  );

  // Save what is left when the screen closes.
  useEffect(
    () => () => {
      if (timer.current) {
        clearTimeout(timer.current);
        void save();
      }
    },
    [save],
  );

  const value = useCallback(
    (lineId: string, field: EditableField) => {
      const d = drafts[fieldKey(field, lineId)];
      if (d !== undefined) return d;
      return script?.lines.find((l) => l.id === lineId)?.[field] ?? '';
    },
    [drafts, script],
  );

  const remove = useCallback(
    async (lineId: string) => {
      await flush();
      const current = scriptRef.current;
      const line = current?.lines.find((l) => l.id === lineId);
      if (!current || !line) return;
      try {
        const res = await api.call(
          'patchScript',
          { reelId },
          { baseVersion: versionRef.current, ops: [{ op: 'removeLine', lineId, expected: lineDigest(line) }] },
        );
        versionRef.current = res.script.version;
        scriptRef.current = res.script;
        setScript(qc, reelId, res.script);
        void qc.invalidateQueries({ queryKey: keys.events(reelId) });
      } catch (e) {
        toast(`Could not remove the line. ${e instanceof Error ? e.message : ''}`.trim());
        await qc.invalidateQueries({ queryKey: keys.reel(reelId) });
      }
    },
    [api, flush, qc, reelId, toast],
  );

  return { value, set, flush, remove, saving };
}
